/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import PostAuthorAndDate from './PostAuthorAndDate';

import {
  setScrollTrueFalse,
  openMessageVideo,
  openImageLightbox,
} from '../../ducks/feedActions';
import {
  imageFlipper,
  pictureSelector,
  transformPostText,
} from '../../helperFunctions';

const FeedPost = styled('div')`
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0px 2px 4px 0px rgb(0 0 0 / 10%);
`;

const FistBumpContainer = styled('div')`
  font-family: 'Nunito Sans', sans-serif;
  display: flex;
  align-items: center;
  margin-right: 18px;
  color: ${(props) => (props.isBumped ? '#ff6600 !important' : '#9e9e9e !important')};
  svg {
    margin-right: 10px;
  }
  .fist-bump-counter {
    margin-right: 5px;
  }
`;

const InteriorDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  padding: 20px;
`;

const MainPostContainer = styled('div')`
  padding: 0px 0px 10px;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const MediaBox = styled('div')`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  max-height: 600px;
  width: 100%;
  position: relative;
`;

const MediaImage = styled('img')`
  max-height: 600px;
  width: 100%;
  object-fit: cover;
`;

const PicAndHeader = styled('div')`
  display: flex;
`;

const PostActions = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: #777;
  font-weight: 400;
  font-size: 13px;
  user-select: none;
  margin-top: 10px;
  margin-left: 20px;
`;

const PostCommentsContainer = styled('div')`
  font-family: 'Nunito Sans', sans-serif;
  display: flex;
  align-items: center;
  margin-right: 18px;
  svg {
    margin-right: 10px;
  }
  .post-comments-counter {
    margin-right: 5px;
  }
`;

const PostText = styled('div')`
  margin: 0 20px 10px 20px;
  white-space: pre-wrap;
  font-size: ${(props) => (props.feedObject.media ? '24px' : '30px')};
`;

const UserPicDiv = styled('div')`
  height: 60px;
  width: 60px;
  border-radius: 50%;
  background-image: ${(feedObject) => `url(${pictureSelector(feedObject.props.userPic, 70, 60)})`};
  background-size: contain;
  min-width: 35px;
  margin-right: 15px;
`;

const VideoIcon = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  width: 100px;
  border-radius: 50%;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.4);
  position: absolute;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  z-index: 1;
`;

const TvPost = ({ feedObject }) => {
  const scrollTrueId = useSelector((state) => state.feed.data.scrollTrueId);
  const bumpCounter = feedObject.fistBumps;
  const isBumped = feedObject.fistBumpActive;

  const dispatch = useDispatch();

  const feedPostRef = useRef();

  useEffect(() => {
    if (feedPostRef.current) {
      if (scrollTrueId === feedObject.id) {
        feedPostRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
        setScrollTrueFalse();
      }
    }
  }, [scrollTrueId]);

  const handleMediaClick = () => {
    if (
      feedObject.media.type === 'IMAGE'
      || feedObject.media.type === 'EXTERNAL_URL'
    ) {
      dispatch(openImageLightbox(feedObject.media.mediaUrl));
    }
  };

  const handleVideoClick = () => {
    dispatch(openMessageVideo(feedObject.media.mediaUrl));
  };

  const renderMediaImage = () => feedObject.media.mediaUrl && (
  <MediaImage src={imageFlipper(feedObject.media)} alt='media' />
  );

  const renderVideoIcon = () => feedObject.media.type === 'VIDEO' && (
  <VideoIcon onClick={handleVideoClick}>
    <IcomoonReact iconSet={iconSet} size={50} icon='altrightarrow' />
  </VideoIcon>
  );

  const renderMedia = () => {
    if (!feedObject.media || !feedObject.media.mediaUrl) return null;

    return (
      <MediaBox onClick={handleMediaClick}>
        {renderMediaImage()}
        {renderVideoIcon()}
      </MediaBox>
    );
  };

  const renderPostActionsSection = () => (
    <PostActions isBumped={isBumped}>
      <FistBumpContainer isBumped={isBumped}>
        <IcomoonReact
          iconSet={iconSet}
          size={50}
          icon={isBumped ? 'fist-bump-full-together' : 'fist-bump-full-apart'}
          color={isBumped ? '#ff6600' : '#9e9e9e'}
        />
        <div className='fist-bump-counter'>{bumpCounter}</div>
        <div>{bumpCounter === 1 ? 'Fist Bump' : 'Fist Bumps'}</div>
      </FistBumpContainer>
      <PostCommentsContainer>
        <IcomoonReact
          iconSet={iconSet}
          size={22}
          icon={feedObject.replyAllowed ? 'comment' : 'comment-disabled'}
          color='#9e9e9e'
        />
        {feedObject.replyAllowed ? (
          <>
            <div className='post-comments-counter'>
              {feedObject.replies.length}
            </div>
            <div>
              {feedObject.replies.length === 1 ? 'Comment' : 'Comments'}
            </div>
          </>
        ) : (
          'Comments Disabled'
        )}
      </PostCommentsContainer>
    </PostActions>
  );

  return (
    <FeedPost>
      <MainPostContainer ref={feedPostRef}>
        <InteriorDiv>
          <PicAndHeader>
            <UserPicDiv props={feedObject} />
            <PostAuthorAndDate feedObject={feedObject} />
          </PicAndHeader>
        </InteriorDiv>
        <PostText feedObject={feedObject}>
          {feedObject.postText
            ? transformPostText(feedObject.postText, true)
            : ''}
        </PostText>
        {renderMedia()}
        {renderPostActionsSection()}
      </MainPostContainer>
    </FeedPost>
  );
};

TvPost.propTypes = {
  feedObject: PropTypes.instanceOf(Object).isRequired,
};

export default TvPost;
